import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthProvider";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { CommandCenterPage } from "@/features/orders-analytics/pages/CommandCenterPage";
import { ExecutivePage } from "@/features/orders-analytics/pages/ExecutivePage";
import { DiagnosticPage } from "@/features/orders-analytics/pages/DiagnosticPage";
import { FunnelSlaPage } from "@/features/orders-analytics/pages/FunnelSlaPage";
import { CorridorsPage } from "@/features/orders-analytics/pages/CorridorsPage";
import { RevenuePage } from "@/features/orders-analytics/pages/RevenuePage";
import { CustomersPage } from "@/features/orders-analytics/pages/CustomersPage";
import { CustomerIntelligencePage } from "@/features/orders-analytics/pages/CustomerIntelligencePage";
import { RiskPage } from "@/features/orders-analytics/pages/RiskPage";
import { MetaGrowthPage } from "@/features/meta-growth/pages/MetaGrowthPage";
import { InvestorsPage } from "@/features/investors/pages/InvestorsPage";
import { ExperimentalPage } from "@/features/orders-analytics/pages/ExperimentalPage";
import { ProjectorHealthPage } from "@/features/observability/pages/ProjectorHealthPage";
import { ShadowParityPage } from "@/features/observability/pages/ShadowParityPage";
import { FrontendErrorsPage } from "@/features/observability/pages/FrontendErrorsPage";
import { PlatformErrorsRoute } from "@/features/observability/platform-errors/access/PlatformErrorsRoute";
import { PlatformErrorsPage } from "@/features/observability/platform-errors/pages/PlatformErrorsPage";
import { AudienceMinerRoute } from "@/features/audience-miner/access/AudienceMinerRoute";
import { Vb360Route } from "@/features/vb360/access/Vb360Route";
import { Loading } from "@/auth/Loading";

const AudienceMinerPage = React.lazy(() => import("@/features/audience-miner/pages/AudienceMinerPage"));
const Vb360SearchPage = React.lazy(() => import("@/features/vb360/pages/Vb360SearchPage"));
const Vb360PersonPage = React.lazy(() => import("@/features/vb360/pages/Vb360PersonPage"));
const SessionDiagnosticsRoute = React.lazy(() => import("@/features/session-diagnostics/access/SessionDiagnosticsRoute"));
const SessionDiagnosticsPage = React.lazy(() => import("@/features/session-diagnostics/pages/SessionDiagnosticsPage"));
const SessionDetailPage = React.lazy(() => import("@/features/session-diagnostics/pages/SessionDetailPage"));

function App() {
    return (
        <AuthProvider>
            <ProtectedRoute>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Navigate to="/command-center" replace />} />
                        <Route path="/command-center" element={<CommandCenterPage />} />
                        <Route path="/executive" element={<ExecutivePage />} />
                        <Route path="/diagnostic" element={<DiagnosticPage />} />
                        <Route path="/diagnostic/sessions" element={<Suspense fallback={<Loading />}><SessionDiagnosticsRoute /></Suspense>}>
                            <Route index element={<Suspense fallback={<Loading />}><SessionDiagnosticsPage /></Suspense>} />
                            <Route path=":segmentId" element={<Suspense fallback={<Loading />}><SessionDetailPage /></Suspense>} />
                        </Route>
                        <Route path="/funnel-sla" element={<FunnelSlaPage />} />
                        <Route path="/corridors" element={<CorridorsPage />} />
                        <Route path="/revenue" element={<RevenuePage />} />
                        <Route path="/customers" element={<CustomersPage />} />
                        <Route path="/customer-intelligence" element={<CustomerIntelligencePage />} />
                        <Route path="/meta-growth" element={<MetaGrowthPage />} />
                        <Route path="/risk" element={<RiskPage />} />
                        <Route path="/investor" element={<InvestorsPage />} />
                        <Route path="/experimental" element={<ExperimentalPage />} />
                        <Route path="/projector-health" element={<ProjectorHealthPage />} />
                        <Route path="/shadow-parity" element={<ShadowParityPage />} />
                        <Route path="/frontend-errors" element={<FrontendErrorsPage />} />
                        <Route path="/platform-errors" element={<PlatformErrorsRoute><PlatformErrorsPage /></PlatformErrorsRoute>} />
                        <Route path="/audience-miner" element={<Navigate to="/audience-miner/summary" replace />} />
                        <Route path="/audience-miner/:view" element={<AudienceMinerRoute><Suspense fallback={<Loading />}><AudienceMinerPage /></Suspense></AudienceMinerRoute>} />
                        <Route path="/vb360" element={<Vb360Route><Suspense fallback={<Loading />}><Vb360SearchPage /></Suspense></Vb360Route>} />
                        <Route path="/vb360/persons/:entityId" element={<Vb360Route><Suspense fallback={<Loading />}><Vb360PersonPage /></Suspense></Vb360Route>} />
                        <Route path="*" element={<Navigate to="/command-center" replace />} />
                    </Routes>
                </BrowserRouter>
            </ProtectedRoute>
        </AuthProvider>
    );
}

export default App;
